import { ImageUp, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { errorMessage, rawFetch, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useEffectiveSettings, useSaveSettings } from "../api/queries";
import { WidgetPreview } from "../components/WidgetPreview";
import { Alert, Button, Card, Field, PageHead, Select, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { contrast, isHex } from "../lib/color";

type Brand = S["BrandSettings"];
type ColorKey = keyof Brand["colors"];

const FONTS = [
  { value: "Inter, system-ui, sans-serif", label: "Inter" },
  { value: "'DM Sans', system-ui, sans-serif", label: "DM Sans" },
  { value: "'Poppins', system-ui, sans-serif", label: "Poppins" },
  { value: "'Montserrat', system-ui, sans-serif", label: "Montserrat" },
  { value: "'Open Sans', system-ui, sans-serif", label: "Open Sans" },
  { value: "'Lato', system-ui, sans-serif", label: "Lato" },
  { value: "'Nunito', system-ui, sans-serif", label: "Nunito" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia (serif)" },
  { value: "system-ui, -apple-system, 'Segoe UI', sans-serif", label: "System font" },
];

const COLOR_FIELDS: { key: ColorKey; label: string; hint: string }[] = [
  { key: "primary", label: "Primary", hint: "Buttons, progress, highlights" },
  { key: "secondary", label: "Secondary", hint: "Headings and company name" },
  { key: "accent", label: "Accent", hint: "Savings and success" },
  { key: "background", label: "Background", hint: "Calculator background" },
  { key: "text", label: "Text", hint: "Body text" },
];

const OPTIONAL_STEPS: { step: S["FlowStep"]; label: string }[] = [
  { step: "roof_pin", label: "Confirm the roof on a map" },
  { step: "usage", label: "Ask for the electricity bill" },
  { step: "roof_type", label: "Ask for the roof type" },
  { step: "contact", label: "Ask for contact details" },
];
const STEP_ORDER: S["FlowStep"][] = ["address", "roof_pin", "usage", "roof_type", "contact", "proposal"];

function ColorField({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <Field label={label} hint={hint} error={!isHex(text) ? "Use a hex colour like #3b5bff" : undefined}>
      <div className="swatch-row">
        <label className="swatch" style={{ background: isHex(value) ? value : "#fff" }} title="Pick a colour">
          <input type="color" value={value.length === 4 ? `#${value.slice(1).split("").map((c) => c + c).join("")}` : value} onChange={(e) => onChange(e.target.value)} />
        </label>
        <input
          className="input mono"
          value={text}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`;
            setText(v);
            if (isHex(v)) onChange(v);
          }}
        />
      </div>
    </Field>
  );
}

function contrastWarnings(c: Brand["colors"]): string[] {
  const out: string[] = [];
  const r = (a: string, b: string) => contrast(a, b);
  if (r(c.text, c.background) < 4.5) out.push(`Text on background is hard to read (contrast ${r(c.text, c.background).toFixed(1)}:1, aim for 4.5:1).`);
  if (r(c.secondary, c.background) < 3) out.push(`Headings (secondary) barely stand out from the background (${r(c.secondary, c.background).toFixed(1)}:1, aim for 3:1).`);
  if (r(c.primary, c.background) < 2) out.push(`Buttons (primary) blend into the background (${r(c.primary, c.background).toFixed(1)}:1).`);
  if (r(c.accent, c.background) < 2) out.push(`Savings figures (accent) are faint on the background (${r(c.accent, c.background).toFixed(1)}:1).`);
  return out;
}

export function Branding() {
  const orgId = useOrgId();
  const { canManage } = useOrg();
  const settings = useEffectiveSettings(orgId);
  const save = useSaveSettings(orgId);
  const toast = useToast();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const saved = settings.data?.documents.brand;

  useEffect(() => {
    if (saved && !brand) setBrand(structuredClone(saved));
  }, [saved, brand]);
  const dirty = useMemo(() => !!brand && JSON.stringify(brand) !== JSON.stringify(saved), [brand, saved]);
  const warnings = brand ? contrastWarnings(brand.colors) : [];

  if (settings.isLoading || !brand) return settings.error ? <Alert tone="danger">{errorMessage(settings.error)}</Alert> : <Spinner />;

  const set = (patch: Partial<Brand>) => setBrand({ ...brand, ...patch });
  const setColor = (key: ColorKey, value: string) => set({ colors: { ...brand.colors, [key]: value } });
  const setText = (key: keyof Brand["texts"], value: string) => set({ texts: { ...brand.texts, [key]: key === "subheadline" ? value || null : value } });
  const setContact = (key: keyof Brand["contact"], value: string) => set({ contact: { ...brand.contact, [key]: value || null } });
  const toggleStep = (step: S["FlowStep"], on: boolean) => {
    const steps = new Set(brand.flow.steps);
    if (on) steps.add(step);
    else steps.delete(step);
    set({ flow: { ...brand.flow, steps: STEP_ORDER.filter((s) => steps.has(s)) } });
  };

  async function upload(file: File) {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      toast("Use a PNG, JPEG or WebP image (SVG is not accepted).", true);
      return;
    }
    if (file.size > 1024 * 1024) {
      toast("The logo must be at most 1 MB.", true);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await rawFetch(`/api/v1/orgs/${orgId}/brand/logo`, { method: "POST", body: form });
      const logo = (await res.json()) as S["BrandLogo"];
      set({ logo_url: logo.url });
      toast("Logo uploaded — save to publish it");
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setUploading(false);
    }
  }

  const saveBar = (
    <div className="row">
      <Button variant="ghost" icon={<RotateCcw />} disabled={!dirty || save.isPending} onClick={() => setBrand(structuredClone(saved!))}>
        Discard
      </Button>
      <Button
        variant="primary"
        icon={<Save />}
        disabled={!dirty || !canManage || COLOR_FIELDS.some((f) => !isHex(brand.colors[f.key])) || !brand.company_name.trim()}
        loading={save.isPending}
        onClick={() =>
          save.mutate(
            { kind: "brand", document: brand },
            { onSuccess: () => toast("Branding saved — your calculator uses it now"), onError: (e) => toast(errorMessage(e), true) },
          )
        }
      >
        Save changes
      </Button>
    </div>
  );

  return (
    <>
      <PageHead title="Branding" description="Make the calculator look and speak like your company. The preview updates as you type." actions={saveBar} />
      {!canManage && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="info">Only owners and admins can change the branding.</Alert>
        </div>
      )}
      <div className="split">
        <div className="stack">
          <Card title="Identity">
            <div className="stack">
              <Field label="Logo" hint="PNG, JPEG or WebP, up to 1 MB. Resized to 512 px.">
                <div
                  className={cx("logo-drop", drag && "drag")}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDrag(true);
                  }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDrag(false);
                    const f = e.dataTransfer.files[0];
                    if (f) void upload(f);
                  }}
                >
                  <div className="logo-box">{brand.logo_url ? <img src={brand.logo_url} alt="Logo" /> : <ImageUp color="var(--faint)" />}</div>
                  <div className="stack" style={{ gap: 8 }}>
                    <span className="small muted">Drop an image here, or</span>
                    <div className="row">
                      <Button size="sm" icon={<ImageUp />} loading={uploading} disabled={!canManage} onClick={() => fileRef.current?.click()}>
                        Upload logo
                      </Button>
                      {brand.logo_url && (
                        <Button size="sm" variant="ghost" icon={<Trash2 />} disabled={!canManage} onClick={() => set({ logo_url: null })}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void upload(f);
                      e.target.value = "";
                    }}
                  />
                </div>
              </Field>
              <div className="form-grid">
                <Field label="Company name" hint="Shown when there is no logo.">
                  <TextInput value={brand.company_name} maxLength={200} onChange={(e) => set({ company_name: e.target.value })} />
                </Field>
                <Field label="Font">
                  <Select
                    value={FONTS.some((f) => f.value === brand.font_family) ? brand.font_family : "__custom"}
                    onChange={(v) => v !== "__custom" && set({ font_family: v })}
                    options={[...FONTS, ...(FONTS.some((f) => f.value === brand.font_family) ? [] : [{ value: "__custom", label: brand.font_family }])]}
                  />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Colours" description="Text on buttons switches between black and white automatically for contrast.">
            <div className="stack">
              <div className="form-grid">
                {COLOR_FIELDS.map((f) => (
                  <ColorField key={f.key} label={f.label} hint={f.hint} value={brand.colors[f.key]} onChange={(v) => setColor(f.key, v)} />
                ))}
              </div>
              {warnings.length > 0 && (
                <Alert tone="warn" title="Contrast:">
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                    {warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </div>
          </Card>

          <Card title="Texts">
            <div className="stack">
              <Field label="Headline">
                <TextInput value={brand.texts.headline} maxLength={200} onChange={(e) => setText("headline", e.target.value)} />
              </Field>
              <Field label="Subheadline">
                <TextInput value={brand.texts.subheadline ?? ""} maxLength={500} onChange={(e) => setText("subheadline", e.target.value)} />
              </Field>
              <Field label="Call to action" hint="The button on the first screen.">
                <TextInput value={brand.texts.call_to_action} maxLength={100} onChange={(e) => setText("call_to_action", e.target.value)} />
              </Field>
            </div>
          </Card>

          <Card title="Contact" description="Shown on proposals, and when the calculator cannot finish a quote.">
            <div className="form-grid">
              <Field label="Email">
                <TextInput type="email" value={brand.contact.email ?? ""} onChange={(e) => setContact("email", e.target.value)} />
              </Field>
              <Field label="Phone">
                <TextInput type="tel" value={brand.contact.phone ?? ""} onChange={(e) => setContact("phone", e.target.value)} />
              </Field>
              <Field label="Website" className="span-2">
                <TextInput type="url" value={brand.contact.url ?? ""} onChange={(e) => setContact("url", e.target.value)} placeholder="https://" />
              </Field>
            </div>
          </Card>

          <Card title="Calculator flow">
            <div>
              <div className="switch-row">
                <div>
                  <b>Show battery options</b>
                  <span className="hint">Offer the batteries set in Rules → Battery.</span>
                </div>
                <Switch checked={brand.flow.show_battery} onChange={(v) => set({ flow: { ...brand.flow, show_battery: v } })} />
              </div>
              <div className="switch-row">
                <div>
                  <b>Show financing</b>
                  <span className="hint">Loan, lease and PPA payments next to the cash price.</span>
                </div>
                <Switch checked={brand.flow.show_financing} onChange={(v) => set({ flow: { ...brand.flow, show_financing: v } })} />
              </div>
              {OPTIONAL_STEPS.map((s) => (
                <div className="switch-row" key={s.step}>
                  <div>
                    <b>{s.label}</b>
                  </div>
                  <Switch checked={brand.flow.steps.includes(s.step)} onChange={(v) => toggleStep(s.step, v)} />
                </div>
              ))}
            </div>
          </Card>
          <div className="spread">
            <span className="hint">{dirty ? "You have unsaved changes." : "All changes saved."}</span>
            {saveBar}
          </div>
        </div>

        <div className="sticky stack">
          <div className="spread">
            <h2>Preview</h2>
            <span className="badge info plain">{dirty ? "Unsaved" : "Live"}</span>
          </div>
          <div className="preview-frame">
            <div className="preview-bar">
              <i />
              <i />
              <i />
              <span>{brand.contact.url?.replace(/^https?:\/\//, "") || "your-website.com"}/solar</span>
            </div>
            <WidgetPreview brand={brand} />
          </div>
        </div>
      </div>
    </>
  );
}
